import React from 'react';

import { Avatar } from '@mui/material';
import { Chip, MenuItem } from 'components';

import { i18n, useTranslation } from 'third-party';

import {
  MEMBER_FILTER_USER_STATUSES,
  UserStatusColors,
} from 'constants/common';

import { useCache } from 'modules/cache';

import { ListFilter } from 'components/Table/Filtering/ListFilter';
import { TypeaheadFilter } from 'components/Table/Filtering/TypeaheadFilter';
import { ColumnFilteringProps, TableColumn } from 'components/Table/types';

import {
  assignableUserRights,
  camelCaseHumanized,
  getAvatarUrl,
} from 'utils/app';

import {
  APIResponse,
  APIUser,
  APIUserOrganization,
  APIUserType,
} from 'types/api';
import { TypeaheadOption } from 'types/app';

export const UserTypeFilter: React.FC<ColumnFilteringProps> = props => {
  const { t } = useTranslation();
  return (
    <ListFilter {...props}>
      <MenuItem key="all" value={undefined}>
        {t('common.all')}
      </MenuItem>
      <MenuItem value={APIUserType.Admin}>Admin</MenuItem>
      <MenuItem value={APIUserType.User}>User</MenuItem>
      <MenuItem value={APIUserType.Editor}>Editor</MenuItem>
    </ListFilter>
  );
};

export const OrganizationFilter: React.FC<ColumnFilteringProps> = props => {
  const { organizationTypeahead } = useCache();

  const loadOrganizations = async (value: string) => {
    const result: APIResponse<{ name: string; id: string }[]> =
      await organizationTypeahead(value);
    let options: TypeaheadOption[] = [];
    if (!result.error) {
      options = result.payload.map(it => ({
        label: it.name,
        value: it.id,
      }));
    }
    return {
      options,
    };
  };

  return <TypeaheadFilter {...props} loadOptions={loadOrganizations} />;
};

export const UserStatusFilter: React.FC<ColumnFilteringProps> = props => {
  const { t } = useTranslation();
  return (
    <ListFilter {...props}>
      <MenuItem key="all" value={undefined}>
        {t('common.all')}
      </MenuItem>
      {MEMBER_FILTER_USER_STATUSES.map(status => (
        <MenuItem key={status} value={status}>
          {t(`membersList.userStatus.${status}`)}
        </MenuItem>
      ))}
    </ListFilter>
  );
};

export const UserRightStatusFilter: React.FC<ColumnFilteringProps> = props => {
  const { t } = useTranslation();
  return (
    <ListFilter {...props}>
      <MenuItem key="all" value={undefined}>
        {t('common.all')}
      </MenuItem>
      {assignableUserRights.map(right => (
        <MenuItem key={right} value={right}>
          {t(`membersList.userRights.${right}`)}
        </MenuItem>
      ))}
    </ListFilter>
  );
};

export const userColumns = (organizationId: string): TableColumn<APIUser>[] => [
  {
    dataId: 'avatar',
    label: 'membersList.column.avatar',
    minWidth: 48,
    width: 64,
    sortable: false,
    resizable: false,
    notFilterable: true,
    cannotBeHidden: false,
    formatter: (_value: any, item: APIUser) => ({
      value: '',
      element: (
        <Avatar
          src={getAvatarUrl(item)}
          alt={`${item.firstName} ${item.lastName}`}
          sx={{ width: 32, height: 32 }}
        />
      ),
    }),
  },
  {
    dataId: 'id',
    label: 'id',
    minWidth: 80,
    width: 140,
    hidden: true,
    sortable: true,
    resizable: true,
  },
  {
    dataId: 'firstName',
    label: 'membersList.column.firstName',
    minWidth: 80,
    width: 140,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'lastName',
    label: 'membersList.column.lastName',
    minWidth: 80,
    width: 140,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'email',
    label: 'membersList.column.email',
    minWidth: 80,
    width: 140,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'phone',
    label: 'membersList.column.phone',
    minWidth: 80,
    width: 140,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'userRights',
    label: 'membersList.column.userRights',
    formatter: (value: APIUserOrganization[], item: APIUser) => {
      return {
        value: item.userOrganizations
          .filter(it => it.organization.id == organizationId)
          .flatMap(it => it?.userRights || [])
          .map(it => (it.right ? camelCaseHumanized(it.right) : ''))
          .join(', '),
      };
    },
    minWidth: 80,
    width: 140,
    sortable: false,
    resizable: true,
    customFilter: UserRightStatusFilter,
  },
  {
    dataId: 'userType',
    label: 'membersList.column.userType',
    formatter: (value: any, item: APIUser) => ({
      value: item.userOrganizations
        .filter(it => it.organization.id == organizationId)
        .map(it => it.userType)
        .join(', '),
    }),
    minWidth: 80,
    width: 140,
    sortable: false,
    resizable: true,
    customFilter: UserTypeFilter,
  },
  {
    dataId: 'userStatus',
    label: 'membersList.column.userStatus',
    formatter: (value: any, item: APIUser) => ({
      value: item.userStatus,
      element: (
        <Chip
          label={i18n.t(`membersList.userStatus.${item.userStatus}`).toString()}
          sx={{ backgroundColor: UserStatusColors[item.userStatus] }}
        />
      ),
    }),
    minWidth: 80,
    width: 140,
    sortable: true,
    resizable: true,
    customFilter: UserStatusFilter,
  },
];
